import { useEffect, useRef, createElement } from "react";
import { queries } from "@/graphql";
import { useQuery } from "@apollo/client/react";
import { enqueueSnackbar } from "notistack";
import { formatTimeAgo } from "@/utils";
import { useNotifyNotification } from "./useNotifyNotification";

/**
 * Custom hook to manage notifications preview, counter, and single oldest real-time notification snackbar.
 * @param showNotificationCounter
 */
export const useNotificationsPreview = (showNotificationCounter: boolean) => {
  const { notifyNotification } = useNotifyNotification();
  const processedNotifyIdsRef = useRef<Set<string | number>>(new Set());
  const lastNotifiedTimeRef = useRef<number>(0);

  const { data } = useQuery<any>(queries.getNewNotifications, {
    pollInterval: 60000,
  });

  const notificationsList =
    data?.getNewNotifications || data?.comGetNewNotifications;
  const counter = notificationsList?.length || 0;

  useEffect(() => {
    const notifications = notificationsList;
    if (
      !notifications ||
      !Array.isArray(notifications) ||
      notifications.length === 0
    ) {
      return;
    }

    const now = Date.now();
    // Enforce at least 50 seconds between snackbar displays (1 notification per 1 minute poll)
    if (now - lastNotifiedTimeRef.current < 50000) {
      return;
    }

    // Filter notifications where isNotify is not true and not already processed in current session
    const unnotifiedList = notifications.filter(
      (n: any) =>
        !n?.isNotify && n?.id && !processedNotifyIdsRef.current.has(n.id),
    );

    if (unnotifiedList.length > 0) {
      // Find single oldest notification (sort by date ascending)
      const oldestNotification = [...unnotifiedList].sort((a: any, b: any) => {
        const timeA = a?.date ? new Date(a.date).getTime() : 0;
        const timeB = b?.date ? new Date(b.date).getTime() : 0;
        return timeA - timeB;
      })[0];

      if (oldestNotification && oldestNotification.id) {
        lastNotifiedTimeRef.current = Date.now();
        processedNotifyIdsRef.current.add(oldestNotification.id);

        if (oldestNotification.Message || oldestNotification.Subject) {
          const subject = oldestNotification.Subject;
          const message = oldestNotification.Message;
          const formattedDate = oldestNotification.date
            ? formatTimeAgo(oldestNotification.date)
            : "";

          const snackbarContent = createElement(
            "div",
            null,
            subject
              ? createElement(
                  "div",
                  {
                    style: {
                      fontWeight: 600,
                      fontSize: "0.95em",
                      marginBottom: "2px",
                    },
                  },
                  subject,
                )
              : null,
            message ? createElement("div", null, message) : null,
            formattedDate
              ? createElement(
                  "div",
                  {
                    style: {
                      fontSize: "0.82em",
                      opacity: 0.85,
                      marginTop: "4px",
                    },
                  },
                  formattedDate,
                )
              : null,
          );

          enqueueSnackbar(snackbarContent, {
            variant: "info",
          });
        }

        notifyNotification({
          notificationId: String(oldestNotification.id),
        });
      }
    }
  }, [data, notifyNotification]);

  return {
    count: showNotificationCounter ? counter : 0,
    previewNotifications: notificationsList?.slice(0, 10) || [],
  };
};
